import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';
import type { SpoolCard } from './SpoolsProduction';
import type { DashboardProject } from '../../../types/project';
import { FATPanel } from './components/panels';
import { FatSpecificationTemplateDto } from '../specifications/Fat/fatSpecificationTemplateTypes';
import { listFatSpecificationTemplates } from '../specifications/Fat/fatSpecificationTemplateApi';

export interface FATSpoolModalProps {
  open: boolean;
  onClose: () => void;
  spool: SpoolCard;
  project?: DashboardProject;
  /** API scope for spool-level FAT (GET/POST /spool-tracker/fat). */
  projectId?: string | number | null;
  pipeId?: string | number | null;
}



/**
 * Spool-scoped Factory Acceptance (FAT) — full-screen overlay, separate from the
 * per-weld workflow step strip in {@link SpoolDetailModal}.
 */
export const FATSpoolModal: React.FC<FATSpoolModalProps> = ({
  open,
  onClose,
  spool,
  project,
  projectId: projectIdProp,
  pipeId: pipeIdProp,
}) => {


  const projectId = projectIdProp ?? project?.project?.projectId as unknown as number;
  const pipeId = pipeIdProp ?? spool.pipeId;
  const [templates, setTemplates] = useState<FatSpecificationTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    let c = true;
    (async () => {
      setLoading(true);
      try {
        const r = await listFatSpecificationTemplates({
          projectId,
          pipeId,
        });
        if (c) setTemplates(r.templates || []);
      } catch {
        if (c) setTemplates([]);
      } finally {
        if (c) setLoading(false);
      }
    })();
    return () => {
      c = false;
    };
  }, [open, projectId, pipeId]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="m-0 h-screen w-screen max-w-none gap-0 rounded-none border-0 bg-white p-0 shadow-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="shrink-0 border-b bg-white px-4 py-4 sm:px-6">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={onClose}
                aria-label="Close FAT"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-800">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-left text-lg font-bold text-gray-900">
                    Factory Acceptance (FAT) — {spool.name}
                  </DialogTitle>
                  {project?.project?.projectName && (
                    <p className="mt-0.5 truncate text-sm text-gray-600">Project: {project.project.projectName}</p>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500 sm:px-6">
                Loading FAT specification…
              </p>
            ) : (
              <div className="mx-auto max-full px-4 py-4 sm:px-6 sm:py-6">
                <FATPanel spool={spool} projectId={projectId} pipeId={pipeId} templates={templates} />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
